import languages from "@/data/languages.json";

export type PairSectionItem = {
  title: string;
  body: string;
};

export type PracticeStackItem = {
  bucket: "active" | "passive";
  subtitle: string;
  title: string;
  description: string;
};

export type PracticePrompt = {
  title: string;
  objective: string;
  reason: string;
};

export type PairFaq = {
  question: string;
  answer: string;
};

export type LanguagePairPageRecord = {
  sourceCode: string;
  sourceName: string;
  sourceSlug: string;
  targetCode: string;
  targetName: string;
  targetSlug: string;
  path: string;
  title: string;
  description: string;
  intro: string;
  stack: PracticeStackItem[];
  searchIntent: PairSectionItem[];
  watchouts: PairSectionItem[];
  prompts: PracticePrompt[];
  faqs: PairFaq[];
  isPublishable: boolean;
  robots: { index: boolean; follow: boolean };
};

type LanguageEntry = {
  name: string;
  code: string;
  slug: string;
};

type ScriptType =
  | "latin"
  | "greek"
  | "cyrillic"
  | "arabic"
  | "hebrew"
  | "han"
  | "kana"
  | "hangul"
  | "indic"
  | "georgian"
  | "armenian"
  | "other";

type LanguageFamily =
  | "germanic"
  | "romance"
  | "slavic"
  | "indo-aryan"
  | "semitic"
  | "sino-tibetan"
  | "japonic"
  | "koreanic"
  | "turkic"
  | "uralic"
  | "hellenic"
  | "austronesian"
  | "tai-kadai"
  | "other";

type LanguageTraits = {
  script: ScriptType;
  family: LanguageFamily;
};

const languageMap = languages as Record<string, string>;

const languageEntries: LanguageEntry[] = Object.entries(languageMap).map(([name, code]) => ({
  name,
  code,
  slug: slugifyLanguageName(name),
}));

const slugToLanguage = Object.fromEntries(
  languageEntries.map((entry) => [entry.slug, entry])
) as Record<string, LanguageEntry>;

const codeToName = Object.fromEntries(
  languageEntries.map((entry) => [entry.code, entry.name])
) as Record<string, string>;

function slugifyLanguageName(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getBaseCode(code: string): string {
  return code.split("-")[0];
}

function scriptLabel(script: ScriptType): string {
  switch (script) {
    case "latin":
      return "Latin script";
    case "greek":
      return "Greek script";
    case "cyrillic":
      return "Cyrillic script";
    case "arabic":
      return "Arabic script";
    case "hebrew":
      return "Hebrew script";
    case "han":
      return "Chinese characters";
    case "kana":
      return "Japanese writing system";
    case "hangul":
      return "Hangul";
    case "indic":
      return "Indic script";
    case "georgian":
      return "Georgian script";
    case "armenian":
      return "Armenian script";
    default:
      return "a different writing system";
  }
}

function detectScript(code: string): ScriptType {
  const baseCode = getBaseCode(code);

  if (
    [
      "en",
      "de",
      "es",
      "fr",
      "pt",
      "it",
      "nl",
      "tr",
      "vi",
      "id",
      "pl",
      "sv",
      "nb",
      "da",
      "fi",
      "sw",
      "af",
      "ms",
      "fil",
      "ca",
      "gl",
      "eu",
      "et",
      "lv",
      "lt",
      "is",
      "mt",
      "sq",
      "uz",
      "tk",
      "hr",
      "cs",
      "hu",
      "ro",
      "sk",
      "sl",
      "ku",
    ].includes(baseCode)
  ) {
    return "latin";
  }

  if (baseCode === "el") {
    return "greek";
  }

  if (["ru", "uk", "bg", "sr", "mk", "kk", "mn", "tt"].includes(baseCode)) {
    return "cyrillic";
  }

  if (["ar", "fa", "ur", "ps"].includes(baseCode)) {
    return "arabic";
  }

  if (baseCode === "he") {
    return "hebrew";
  }

  if (["cmn", "yue"].includes(baseCode)) {
    return "han";
  }

  if (baseCode === "ja") {
    return "kana";
  }

  if (baseCode === "ko") {
    return "hangul";
  }

  if (["hi", "bn", "gu", "kn", "ml", "mr", "ta", "te", "ne", "si"].includes(baseCode)) {
    return "indic";
  }

  if (baseCode === "ka") {
    return "georgian";
  }

  if (baseCode === "hy") {
    return "armenian";
  }

  return "other";
}

function detectFamily(code: string): LanguageFamily {
  const baseCode = getBaseCode(code);

  if (["en", "de", "nl", "sv", "nb", "da", "is", "af"].includes(baseCode)) {
    return "germanic";
  }

  if (["es", "fr", "pt", "it", "ca", "gl", "ro"].includes(baseCode)) {
    return "romance";
  }

  if (["pl", "ru", "uk", "bg", "hr", "cs", "sr", "sk", "sl", "mk"].includes(baseCode)) {
    return "slavic";
  }

  if (["hi", "bn", "gu", "mr", "ne", "si", "ur"].includes(baseCode)) {
    return "indo-aryan";
  }

  if (["ar", "he", "am"].includes(baseCode)) {
    return "semitic";
  }

  if (["cmn", "yue", "my"].includes(baseCode)) {
    return "sino-tibetan";
  }

  if (baseCode === "ja") {
    return "japonic";
  }

  if (baseCode === "ko") {
    return "koreanic";
  }

  if (["tr", "kk", "uz", "tk", "tt"].includes(baseCode)) {
    return "turkic";
  }

  if (["fi", "hu", "et"].includes(baseCode)) {
    return "uralic";
  }

  if (baseCode === "el") {
    return "hellenic";
  }

  if (["id", "ms", "fil"].includes(baseCode)) {
    return "austronesian";
  }

  if (["th", "lo"].includes(baseCode)) {
    return "tai-kadai";
  }

  return "other";
}

function buildTraits(code: string): LanguageTraits {
  return {
    script: detectScript(code),
    family: detectFamily(code),
  };
}

function buildIntro(
  targetName: string,
  sourceName: string,
  sourceTraits: LanguageTraits,
  targetTraits: LanguageTraits
): string {
  const scriptSentence =
    sourceTraits.script === targetTraits.script
      ? `${sourceName} and ${targetName} share ${scriptLabel(targetTraits.script).toLowerCase()}, which makes this pair easier to enter but also easier to over-trust at the spelling level.`
      : `${sourceName} and ${targetName} do not use the same writing system, so this page is designed to get the learner into speaking mode quickly before script friction slows them down.`;

  const familySentence =
    sourceTraits.family === targetTraits.family
      ? `Because the two languages are closer in family, the useful discipline is to test familiar-looking words and patterns in live speech instead of assuming they transfer cleanly.`
      : `Because the two languages sit farther apart, the useful move is to build a small set of reusable sentence frames and repeat them until the target language feels normal in the mouth.`;

  return `This page opens Chickytutor with ${targetName} as the target language and ${sourceName} as the support language, so the learner lands on the real app first and can start speaking immediately. Below the app, the page gives a practical stack for building ${targetName} with short speaking loops, passive immersion, and repeatable prompts that still work even without a hand-written curriculum for this exact pair. ${scriptSentence} ${familySentence}`;
}

function buildStack(targetName: string): PracticeStackItem[] {
  return [
    {
      bucket: "active",
      subtitle: "AI speaking",
      title: `${targetName} speaking loops in Chickytutor`,
      description: `Use short live conversations in ${targetName} to practice introductions, requests, repairs, and everyday turns until the language starts to come out without translation lag.`,
    },
    {
      bucket: "active",
      subtitle: "Active recall",
      title: `${targetName} sentence mining`,
      description: `Collect short sentences that you can actually imagine saying in ${targetName}. Reuse them until they become default building blocks instead of isolated vocabulary.`,
    },
    {
      bucket: "active",
      subtitle: "Pronunciation",
      title: `${targetName} shadowing in small bursts`,
      description: `Repeat short lines of audio aloud. The goal is not perfection; it is to make ${targetName} rhythm and mouth movement feel less foreign before longer conversations.`,
    },
    {
      bucket: "passive",
      subtitle: "Video",
      title: `${targetName} video immersion`,
      description: `Watch short clips, simple interviews, or subtitles-first shows in ${targetName}. Treat this as ear training, not a test of full comprehension.`,
    },
    {
      bucket: "passive",
      subtitle: "Audio",
      title: `${targetName} podcasts and repeat listening`,
      description: `Cycle through podcasts, learner audio, or short native clips in ${targetName}. Re-listening is useful because recognition grows faster than one-pass exposure.`,
    },
    {
      bucket: "passive",
      subtitle: "Reading",
      title: `${targetName} micro-reading`,
      description: `Read short dialogues, captions, or graded snippets in ${targetName}. This stabilizes common sentence shapes and keeps vocabulary tied to context.`,
    },
  ];
}

function buildWatchouts(
  sourceName: string,
  targetName: string,
  sourceTraits: LanguageTraits,
  targetTraits: LanguageTraits
): PairSectionItem[] {
  const watchouts: PairSectionItem[] = [];

  if (sourceTraits.script !== targetTraits.script) {
    watchouts.push({
      title: "Do not transliterate in your head",
      body: `${sourceName} uses ${scriptLabel(sourceTraits.script).toLowerCase()} while ${targetName} uses ${scriptLabel(targetTraits.script).toLowerCase()}. Move into the target writing system early instead of mentally rewriting everything back into ${sourceName}.`,
    });
  } else {
    watchouts.push({
      title: "Shared script does not mean shared pronunciation",
      body: `${sourceName} and ${targetName} look easier because they share ${scriptLabel(targetTraits.script).toLowerCase()}, but spelling habits transfer fast. Keep checking how ${targetName} sounds, not just how it looks.`,
    });
  }

  if (sourceTraits.family === targetTraits.family) {
    watchouts.push({
      title: "Similarity can create false confidence",
      body: `When ${sourceName} and ${targetName} are closer in family, it is tempting to trust look-alike words and sentence patterns. Use live speaking reps to confirm what is actually natural in ${targetName}.`,
    });
  } else {
    watchouts.push({
      title: "Build around sentence frames, not word lists",
      body: `${sourceName} and ${targetName} are far enough apart that waiting for obvious cognates slows you down. Use a small set of high-frequency sentence frames and keep recycling them in real conversation.`,
    });
  }

  watchouts.push({
    title: `Keep ${sourceName} for support, not for output`,
    body: `Use ${sourceName} to clarify a word, a task, or a correction, then switch the speaking work back into ${targetName}. That balance keeps the session usable without turning it into translation practice.`,
  });

  watchouts.push({
    title: `Train your ear for ${targetName} before chasing perfection`,
    body: `Listening tolerance matters more than full accuracy at the start. Spend daily time with short, comprehensible ${targetName} audio so the sound system stops feeling unfamiliar under pressure.`,
  });

  watchouts.push({
    title: "Keep early speaking tasks small and reusable",
    body: `The fastest early progress comes from repeating a few functional tasks: introducing yourself, asking for help, making requests, repairing misunderstandings, and describing simple routines in ${targetName}.`,
  });

  return watchouts;
}

function buildSearchIntent(sourceName: string, targetName: string): PairSectionItem[] {
  return [
    {
      title: `If you searched for "${sourceName} to ${targetName}"`,
      body: `Use this page when you want to learn ${targetName} with ${sourceName} support, not just translate one isolated word. Start the speaking session, ask for a short ${sourceName} explanation when needed, then repeat the corrected ${targetName} phrase aloud.`,
    },
    {
      title: `If you searched for "${targetName} in ${sourceName}"`,
      body: `The useful next step is usually a phrase you can say, not a dictionary-style answer. Ask the tutor for the ${targetName} version of one practical sentence, then practice a tiny roleplay so the phrase turns into usable speech.`,
    },
    {
      title: `If you searched whether ${sourceName} and ${targetName} are similar`,
      body: `Similarity helps only when it survives real output. Use the watchouts below to spot transfer traps, then test one sentence frame in a spoken loop instead of relying on look-alike words.`,
    },
  ];
}

function buildPrompts(targetName: string): PracticePrompt[] {
  return [
    {
      title: "Self-introduction loop",
      objective: `Introduce yourself in ${targetName}, say where you are from, what you do, and why you are learning the language.`,
      reason:
        "This gives you a repeatable opening routine that can anchor every new speaking session.",
    },
    {
      title: "Coffee or food order",
      objective: `Practice ordering one or two items in ${targetName}, asking for a change, and confirming the final order.`,
      reason:
        "Short transactional language is high frequency and teaches useful sentence control without heavy vocabulary load.",
    },
    {
      title: "Directions and location help",
      objective: `Ask where something is in ${targetName}, confirm left or right, and repeat the directions back.`,
      reason:
        "Direction language forces listening, clarification, and short memory-based output in one drill.",
    },
    {
      title: "Daily routine",
      objective: `Describe a normal day in ${targetName} from morning to evening using simple time markers and repeated verbs.`,
      reason:
        "Routine talk turns vocabulary into connected speech instead of disconnected flashcard knowledge.",
    },
    {
      title: "Repair a misunderstanding",
      objective: `Ask for repetition, say you do not understand, and request a slower explanation in ${targetName}.`,
      reason:
        "Repair phrases keep conversations alive and reduce the temptation to abandon the target language.",
    },
    {
      title: "Weekend plans",
      objective: `Explain what you want to do this weekend in ${targetName}, then ask the tutor a follow-up question.`,
      reason:
        "This is a natural way to practice future meaning, preferences, and conversational follow-through.",
    },
    {
      title: "Describe a short video clip",
      objective: `Watch a short clip in ${targetName} and retell what happened using very simple sentences.`,
      reason:
        "Retelling connects passive immersion with active recall and shows where vocabulary gaps actually matter.",
    },
    {
      title: "Opinion with one reason",
      objective: `State a simple opinion in ${targetName} and support it with one clear reason and one example.`,
      reason:
        "This upgrades you from sentence fragments to connected thought without making the task too complex.",
    },
  ];
}

function buildFaqs(sourceName: string, targetName: string): PairFaq[] {
  return [
    {
      question: `Can I learn ${targetName} from ${sourceName} with Chickytutor?`,
      answer: `Yes. This page opens Chickytutor with ${targetName} as the target language and ${sourceName} as the support language, so you can start speaking immediately with the right setup.`,
    },
    {
      question: `What if ${sourceName} and ${targetName} use different writing systems?`,
      answer: `That is still workable. The best approach is to keep the speaking sessions short, practice high-frequency phrases, and spend a little daily time with the ${targetName} writing system so it stops feeling like a separate task.`,
    },
    {
      question: `Should I keep using ${sourceName} during the session?`,
      answer: `Use ${sourceName} for clarification when needed, but keep the output in ${targetName}. The goal is to make ${targetName} carry the speaking load while ${sourceName} reduces friction.`,
    },
    {
      question: `Does this page open the same app as the homepage?`,
      answer: `Yes. This route uses the normal Chickytutor app and preselects the language pair for you. The extra content below the fold is there to make the page more useful for practice and search.`,
    },
  ];
}

export function getLanguagePairPage(
  targetSlug: string,
  sourceSlug: string
): LanguagePairPageRecord | null {
  const targetEntry = slugToLanguage[targetSlug];
  const sourceEntry = slugToLanguage[sourceSlug];

  if (!targetEntry || !sourceEntry) {
    return null;
  }

  if (targetEntry.code === sourceEntry.code) {
    return null;
  }
  const sourceTraits = buildTraits(sourceEntry.code);
  const targetTraits = buildTraits(targetEntry.code);

  return {
    sourceCode: sourceEntry.code,
    sourceName: sourceEntry.name,
    sourceSlug: sourceEntry.slug,
    targetCode: targetEntry.code,
    targetName: targetEntry.name,
    targetSlug: targetEntry.slug,
    path: `/learn/${targetEntry.slug}/from/${sourceEntry.slug}`,
    title: `Learn ${targetEntry.name} from ${sourceEntry.name} | Chickytutor`,
    description: `Open Chickytutor with ${targetEntry.name} preselected and use a practical stack, source-aware watchouts, and repeatable prompts to build ${targetEntry.name} from ${sourceEntry.name}.`,
    intro: buildIntro(targetEntry.name, sourceEntry.name, sourceTraits, targetTraits),
    stack: buildStack(targetEntry.name),
    searchIntent: buildSearchIntent(sourceEntry.name, targetEntry.name),
    watchouts: buildWatchouts(sourceEntry.name, targetEntry.name, sourceTraits, targetTraits),
    prompts: buildPrompts(targetEntry.name),
    faqs: buildFaqs(sourceEntry.name, targetEntry.name),
    isPublishable: true,
    robots: {
      index: true,
      follow: true,
    },
  };
}

export function getPublishableLanguagePairPages(): LanguagePairPageRecord[] {
  const pages: LanguagePairPageRecord[] = [];

  for (const targetEntry of languageEntries) {
    for (const sourceEntry of languageEntries) {
      if (targetEntry.code === sourceEntry.code) {
        continue;
      }

      const page = getLanguagePairPage(targetEntry.slug, sourceEntry.slug);

      if (page) {
        pages.push(page);
      }
    }
  }

  return pages;
}

export function getRelatedPublishablePairs(
  record: LanguagePairPageRecord,
  limit: number = 6
): LanguagePairPageRecord[] {
  return getPublishableLanguagePairPages()
    .filter((page) => page.path !== record.path)
    .sort((a, b) => {
      const aScore =
        Number(a.targetCode === record.targetCode) + Number(a.sourceCode === record.sourceCode);
      const bScore =
        Number(b.targetCode === record.targetCode) + Number(b.sourceCode === record.sourceCode);

      if (aScore !== bScore) {
        return bScore - aScore;
      }

      return a.title.localeCompare(b.title);
    })
    .slice(0, limit);
}

export function getLanguagePairStaticParams(): Array<{ source: string; target: string }> {
  return [];
}

export function getLanguagePairNameByCode(code: string): string | undefined {
  return codeToName[code];
}
